import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {  SiteFormData } from '../../../models/site.model';
import {  RegionFormData } from '../../../models/region.model';
import { UserFormData } from '../../../models/user.model';
import { ValidationService } from '../../../services/validation.service';
import { IdGeneratorService } from '../../../services/shared/id-generator/id-generator.service';
import { ConfirmComponent } from '../../dialogs/confirm/confirm.component';
import { faMapMarkerAlt, faPlus, faEdit, faSave, faTimes, faTrash, faUser, faUserPlus, faChevronDown, faChevronUp, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

export interface SiteOperatorAssignment {
  siteId: number | string | null;
  operatorId: number | string | null;
  siteName: string;
  operatorName: string;
}

export interface SitesManagerChangeEvent {
  sites: SiteFormData[];
  operatorAssignments: SiteOperatorAssignment[];
  isValid: boolean;
  errors: { [key: string]: string };
}

export interface AssignExistingOperatorEvent {
  operatorId: number | string;
  siteId: number | string | null;
  siteName: string;
}

export interface RemoveOperatorFromSiteEvent {
  operatorId: number | string;
  siteId: number | string | null;
  siteName: string;
}

export interface CreateOperatorForSiteEvent {
  siteId: number | string | null;
  siteName: string;
  operatorData: {
    username: string;
    fullName: string;
    email: string;
    password: string;
    mobile?: string;
  };
}

export interface SiteRemovalRequestEvent {
  siteIndex: number;
  siteId: number | string | null;
  siteName: string;
  assignedOperators: UserFormData[];
}

export interface SiteRemovalConfirmedEvent {
  siteIndex: number;
  siteId: number | string | null;
  siteName: string;
  operatorsToReassign: UserFormData[];
  removeAction: 'reassign' | 'unassign';
}

export interface OperatorReassignmentEvent {
  operatorIds: (number | string)[];
  fromSiteId: number | string | null;
  toSiteId?: number | string | null;
  action: 'unassign' | 'reassign';
}

export interface EditOperatorEvent {
  operatorId: number | string;
  operatorData: {
    username: string;
    fullName: string;
    password?: string;
    email: string;
    mobile?: string;
    emp_code?: string;
  };
}

export interface DeleteOperatorEvent {
  operatorId: number | string;
  operatorName: string;
}

@Component({
  selector: 'app-sites-manager',
  templateUrl: './sites-manager.component.html',
  styleUrls: ['./sites-manager.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SitesManagerComponent),
      multi: true
    }
  ]
})
export class SitesManagerComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() editMode: boolean = false;
  @Input() sitesData: SiteFormData[] = [];
  @Input() operatorsData: UserFormData[] = [];
  @Input() regionsData: RegionFormData[] = []; // Available regions for site assignment
  @Input() showValidationErrors: boolean = false;
  @Input() disabled: boolean = false;
  @Input() regionId: number | string | null = null; // Sites belong to a specific region

  @Output() sitesChange = new EventEmitter<SitesManagerChangeEvent>();
  @Output() validationChange = new EventEmitter<boolean>();
  @Output() assignExistingOperator = new EventEmitter<AssignExistingOperatorEvent>();
  @Output() removeOperatorFromSite = new EventEmitter<RemoveOperatorFromSiteEvent>();
  @Output() createOperatorForSite = new EventEmitter<CreateOperatorForSiteEvent>();
  @Output() siteRemovalRequest = new EventEmitter<SiteRemovalRequestEvent>();
  @Output() siteRemovalConfirmed = new EventEmitter<SiteRemovalConfirmedEvent>();
  @Output() operatorReassignment = new EventEmitter<OperatorReassignmentEvent>();
  @Output() editOperatorConfirmed = new EventEmitter<EditOperatorEvent>();
  @Output() deleteOperatorConfirmed = new EventEmitter<DeleteOperatorEvent>();

  // Icons
  faMapMarkerAlt = faMapMarkerAlt;
  faPlus = faPlus;
  faEdit = faEdit;
  faSave = faSave;
  faTimes = faTimes;
  faTrash = faTrash;
  faUser = faUser;
  faUserPlus = faUserPlus;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faExclamationTriangle = faExclamationTriangle;

  sitesForm: FormGroup;
  newOperatorForm: FormGroup;
  editOperatorForm: FormGroup;
  
  // UI State for inline operations
  showAssignmentDropdown: { [siteIndex: number]: boolean } = {};
  showCreateOperatorModal: boolean = false;
  showEditOperatorModal: boolean = false;
  currentSiteForOperator: { id: number | string | null; name: string; index: number } | null = null;
  currentOperatorForEdit: UserFormData | null = null;
  
  private destroy$ = new Subject<void>();
  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(
    private fb: FormBuilder,
    private validationService: ValidationService,
    private idGeneratorService: IdGeneratorService,
    private dialog: MatDialog
  ) {
    this.createForm();
    this.createNewOperatorForm();
    this.createEditOperatorForm();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.setupFormSubscription();
    
    // Add document click listener to close dropdowns
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Remove document click listener
    document.removeEventListener('click', this.onDocumentClick.bind(this));
  }

  // Close dropdowns when clicking outside
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.operator-assignment-dropdown')) {
      this.closeAllDropdowns();
    }
  }

  private createForm(): void {
    this.sitesForm = this.fb.group({
      sites: this.fb.array([])
    });
  }

  private createNewOperatorForm(): void {
    this.newOperatorForm = this.fb.group({
      fullName: ['', [Validators.required, Validators.minLength(2)]],
      username: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      mobile: ['',Validators.pattern(/^[0-9+\-\s()]+$/)],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  private createEditOperatorForm(): void {
    this.editOperatorForm = this.fb.group({
      fullName: ['', [Validators.required, Validators.minLength(2)]],
      username: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      mobile: ['',Validators.pattern(/^[0-9+\-\s()]+$/)],
      password: ['', [Validators.required, Validators.minLength(6)]],
      emp_code: ['']
    });
  }

  private initializeForm(): void {
    const sitesArray = this.sitesForm.get('sites') as FormArray;
    
    // Clear existing controls
    while (sitesArray.length !== 0) {
      sitesArray.removeAt(0);
    }

    // Add existing sites or create first empty site
    if (this.sitesData && this.sitesData.length > 0) {
      this.sitesData.forEach(site => {
        sitesArray.push(this.createSiteFormGroup(site));
      });
    } else if (!this.editMode) {
      // Add one empty site for create mode
      sitesArray.push(this.createSiteFormGroup());
    }

    if (this.disabled) {
      this.sitesForm.disable();
    } else {
      this.sitesForm.enable();
    }
  }

  private createSiteFormGroup(siteData?: SiteFormData): FormGroup {
    const formGroup = this.fb.group({
      id: [siteData && siteData.id || this.idGeneratorService.generateAlphanumericId()], // Generate temporary unique alphanumeric ID for new sites
      name: [
        siteData && siteData.name || '',
        [Validators.required, Validators.minLength(2), Validators.maxLength(100)]
      ],
      region_id: [
        siteData && siteData.region_id || this.regionId || '',
        [Validators.required]
      ],
      regionName: [siteData && siteData.regionName || ''],
      isNew: [siteData && siteData.isNew !== undefined ? siteData.isNew : true]
    });

    return formGroup;
  }



  private setupFormSubscription(): void {
    this.sitesForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitChanges();
      });

    this.sitesForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.validationChange.emit(this.sitesForm.valid);
      });
  }

  private emitChanges(): void {
    const formValue = this.sitesForm.value;
    const sites: SiteFormData[] = formValue.sites || [];
    const operatorAssignments = this.getOperatorAssignments();
    const isValid = this.sitesForm.valid;
    const errors = this.getFormErrors();

    const changeEvent: SitesManagerChangeEvent = {
      sites,
      operatorAssignments,
      isValid,
      errors
    };

    this.sitesChange.emit(changeEvent);
    this.onChange(changeEvent);
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (value && Array.isArray(value)) {
      this.sitesData = value;
      this.initializeForm();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.sitesForm.disable();
    } else {
      this.sitesForm.enable();
    }
  }

  // Site management methods
  get sitesArray(): FormArray {
    return this.sitesForm.get('sites') as FormArray;
  }

  addSite(): void {
    const newSiteGroup = this.createSiteFormGroup();
    this.sitesArray.push(newSiteGroup);
    
    // Focus on the new site's name field after a short delay
    setTimeout(() => {
      const newIndex = this.sitesArray.length - 1;
      const nameInput = document.getElementById(`site_name_${newIndex}`);
      if (nameInput) {
        nameInput.focus();
      }
    }, 100);
  }

  // Operator cleanup and site removal methods
  private getOperatorsToCleanup(siteId: number | string | null): UserFormData[] {
    return this.operatorsData.filter(operator => 
      operator.userType === 'operator' && 
      operator.siteIds && 
      operator.siteIds.some(id => String(id) === String(siteId))
    );
  }

  private canRemoveSiteWithOperators(siteIndex: number): boolean {
    const siteId = this.getSiteId(siteIndex);
    const assignedOperators = this.getOperatorsToCleanup(siteId);
    return assignedOperators.length === 0;
  }

  private showSiteRemovalConfirmation(siteIndex: number): void {
    const siteId = this.getSiteId(siteIndex);
    const siteName = this.getSiteName(siteIndex);
    const assignedOperators = this.getOperatorsToCleanup(siteId);
    
    let dialogData;
    
    if (assignedOperators.length === 0) {
      // No operators assigned - simple confirmation
      dialogData = {
        title: 'Remove Site',
        message: `Are you sure you want to remove "${siteName}"?`,
        type: 'warning' as const,
        confirmText: 'Remove Site',
        cancelText: 'Cancel'
      };
    } else {
      // Has assigned operators - show warning with details
      const operatorNames = assignedOperators.map(op => `${op.userDetails.name} (@${op.username})`);
      dialogData = {
        title: 'Remove Site with Assigned Operators',
        message: `Removing "${siteName}" will affect ${assignedOperators.length} assigned operator(s). What would you like to do with the assigned operators?`,
        details: operatorNames,
        type: 'warning' as const,
        confirmText: 'Remove Site & Unassign Operators',
        cancelText: 'Cancel',
        showDetails: true
      };
    }
    
    const dialogRef = this.dialog.open(ConfirmComponent, {
      data: dialogData,
      width: '450px',
      disableClose: true
    });
    
    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.handleConfirmedSiteRemoval(siteIndex, assignedOperators);
      }
    });
  }

  private handleConfirmedSiteRemoval(siteIndex: number, assignedOperators: UserFormData[]): void {
    const siteId = this.getSiteId(siteIndex);
    const siteName = this.getSiteName(siteIndex);
    
    // Emit events for parent to handle operator cleanup
    if (assignedOperators.length > 0) {
      const operatorIds = assignedOperators.map(op => op.id).filter(id => id !== undefined && id !== null) as (number | string)[];
      
      // Emit operator reassignment event (unassign operators)
      this.operatorReassignment.emit({
        operatorIds,
        fromSiteId: siteId,
        action: 'unassign'
      });
      
      // Emit site removal confirmed event
      this.siteRemovalConfirmed.emit({
        siteIndex,
        siteId,
        siteName,
        operatorsToReassign: assignedOperators,
        removeAction: 'unassign'
      });
    }
    
    // Proceed with actual site removal
    this.performSiteRemoval(siteIndex);
  }

  private performSiteRemoval(index: number): void {
    if (this.sitesArray.length > 1) {
      this.sitesArray.removeAt(index);
      this.emitChanges();
    }
  }

  removeSite(index: number): void {
    if (this.sitesArray.length > 1) {
      // Emit site removal request event first
      const siteId = this.getSiteId(index);
      const siteName = this.getSiteName(index);
      const assignedOperators = this.getOperatorsToCleanup(siteId);
      
      this.siteRemovalRequest.emit({
        siteIndex: index,
        siteId,
        siteName,
        assignedOperators
      });
      
      // Show confirmation dialog
      this.showSiteRemovalConfirmation(index);
    }
  }

  private markSiteAsTouched(index: number): void {
    const siteGroup = this.sitesArray.at(index) as FormGroup;
    if (siteGroup) {
      Object.keys(siteGroup.controls).forEach(key => {
        const control = siteGroup.get(key);
        if (control) {
          control.markAsTouched();
        }
      });
    }
  }

  onRegionChange(index: number, event: Event): void {
    const target = event.target as HTMLSelectElement;
    const regionId = target.value || null;
    const siteGroup = this.sitesArray.at(index) as FormGroup;
    
    if (siteGroup && regionId) {
      // Find the selected region to get its name
      // Handle both string (alphanumeric) and numeric IDs for backward compatibility
      const selectedRegion = this.regionsData.find(region => {
        // Convert both IDs to string for comparison to handle mixed ID types
        return String(region.id) === String(regionId);
      });
      
      siteGroup.patchValue({
        region_id: regionId,
        regionName: selectedRegion ? selectedRegion.name : ''
      });
    }
  }

  // Operator assignment methods - Note: multiple operators can be assigned to a site
  getOperatorsForSite(siteId: number | string | null): UserFormData[] {
    return this.operatorsData.filter(operator => 
      operator.userType === 'operator' && 
      operator.siteIds && 
      operator.siteIds.some(id => String(id) === String(siteId))
    );
  }

  // Backward compatibility method - returns first operator or null
  getOperatorForSite(siteId: number | string | null): UserFormData | null {
    const operators = this.getOperatorsForSite(siteId);
    return operators.length > 0 ? operators[0] : null;
  }

  getUnassignedOperators(): UserFormData[] {
    return this.operatorsData.filter(operator => 
      operator.userType === 'operator' && 
      (!operator.siteIds || operator.siteIds.length === 0)
    );
  }

  getTotalAssignedOperators(): number {
    return this.operatorsData.filter(operator => 
      operator.userType === 'operator' && 
      operator.siteIds && 
      operator.siteIds.length > 0
    ).length;
  }

  private getOperatorAssignments(): SiteOperatorAssignment[] {
    const assignments: SiteOperatorAssignment[] = [];
    const sites = this.sitesForm.value.sites || [];

    sites.forEach((site: any) => {
      const assignedOperators = this.getOperatorsForSite(site.id);
      
      if (assignedOperators.length === 0) {
        // Site with no operators assigned
        assignments.push({
          siteId: site.id,
          operatorId: null,
          siteName: site.name,
          operatorName: ''
        });
      } else {
        // Site with assigned operators - create one assignment per operator
        assignedOperators.forEach(operator => {
          assignments.push({
            siteId: site.id,
            operatorId: operator.id || null,
            siteName: site.name,
            operatorName: operator.username || ''
          });
        });
      }
    });

    return assignments;
  }

  getAvailableOperatorsForSite(siteId?: number | string | null): UserFormData[] {
    return this.operatorsData.filter(operator => 
      operator.userType === 'operator' && 
      (!operator.siteIds || operator.siteIds.length === 0 )
    );
  }

  handleAssignOperatorClick(siteIndex: number): void {
    const siteId = this.getSiteId(siteIndex);
    const availableOperators = this.getAvailableOperatorsForSite(siteId);
    
    // If no operators available, open create operator modal directly
    if (availableOperators.length === 0) {
      this.openCreateOperatorModal(siteIndex);
    } else {
      // Show dropdown with available operators
      this.toggleAssignmentDropdown(siteIndex);
    }
  }

  // Toggle assignment dropdown
  toggleAssignmentDropdown(siteIndex: number): void {
    // Close all other dropdowns first
    Object.keys(this.showAssignmentDropdown).forEach(key => {
      if (parseInt(key) !== siteIndex) {
        this.showAssignmentDropdown[parseInt(key)] = false;
      }
    });
    
    this.showAssignmentDropdown[siteIndex] = !this.showAssignmentDropdown[siteIndex];
  }

  // Close all dropdowns
  closeAllDropdowns(): void {
    this.showAssignmentDropdown = {};
  }

  // Assign existing operator to site
  assignExistingOperatorToSite(operatorId: number | string, siteIndex: number): void {
    const siteId = this.getSiteId(siteIndex);
    const siteName = this.getSiteName(siteIndex);
    
    this.assignExistingOperator.emit({
      operatorId,
      siteId,
      siteName
    });
    
    this.closeAllDropdowns();
  }

  // Remove operator from site
  removeOperatorFromThisSite(operatorId: number | string, siteIndex: number): void {
    const siteId = this.getSiteId(siteIndex);
    const siteName = this.getSiteName(siteIndex);
    
    this.removeOperatorFromSite.emit({
      operatorId,
      siteId,
      siteName
    });
  }

  // Open create operator modal
  openCreateOperatorModal(siteIndex: number): void {
    const siteId = this.getSiteId(siteIndex);
    const siteName = this.getSiteName(siteIndex);
    
    this.currentSiteForOperator = {
      id: siteId,
      name: siteName,
      index: siteIndex
    };
    
    this.showCreateOperatorModal = true;
    this.closeAllDropdowns();
    this.newOperatorForm.reset();
  }

  // Close create operator modal
  closeCreateOperatorModal(): void {
    this.showCreateOperatorModal = false;
    this.currentSiteForOperator = null;
    this.newOperatorForm.reset();
  }

  // Create operator for current site
  createOperatorForCurrentSite(): void {
    if (this.newOperatorForm.valid && this.currentSiteForOperator) {
      const formValue = this.newOperatorForm.value;
      
      this.createOperatorForSite.emit({
        siteId: this.currentSiteForOperator.id,
        siteName: this.currentSiteForOperator.name,
        operatorData: {
          username: formValue.username,
          fullName: formValue.fullName,
          email: formValue.email,
          password: formValue.password,
          mobile: formValue.mobile
        }
      });
      
      this.closeCreateOperatorModal();
    }
  }

  // Edit operator modal methods
  openEditOperatorModal(operator: UserFormData): void {
    this.currentOperatorForEdit = operator;
    const operatorData = {
      fullName: operator.userDetails && operator.userDetails.name ? operator.userDetails.name : '',
      username: operator.username || '',
      password: operator.password || '',
      email: operator.userDetails && operator.userDetails.email ? operator.userDetails.email : '',
      mobile: operator.userDetails && operator.userDetails.mobile ? operator.userDetails.mobile : '',
    };
    this.editOperatorForm.patchValue(operatorData);
    this.showEditOperatorModal = true;
  }

  closeEditOperatorModal(): void {
    this.showEditOperatorModal = false;
    this.currentOperatorForEdit = null;
    this.editOperatorForm.reset();
  }

  updateCurrentOperator(): void {
    if (this.editOperatorForm.valid && this.currentOperatorForEdit) {
      const formValue = this.editOperatorForm.value;
      
      this.editOperatorConfirmed.emit({
        operatorId: this.currentOperatorForEdit.id,
        operatorData: {
          username: formValue.username,
          fullName: formValue.fullName,
          email: formValue.email,
          mobile: formValue.mobile,
          emp_code: formValue.emp_code
        }
      });
      
      this.closeEditOperatorModal();
    }
  }
  mobileFieldError(fieldName: string): boolean {
    const field = this.newOperatorForm.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }
  deleteOperator(operator: UserFormData): void {
    const isUnassigned = !operator.siteIds || operator.siteIds.length === 0;
    
    if (!isUnassigned) {
      alert('Cannot delete operator that is assigned to sites. Please remove from all sites first.');
      return;
    }
    
    const operatorName = operator.userDetails && operator.userDetails.name ? operator.userDetails.name : operator.username;
    const message = 'Are you sure you want to permanently delete operator "' + operatorName + '"?\n\nThis action cannot be undone.';
    const confirmed = confirm(message);
    
    if (confirmed) {
      const deleteEvent = {
        operatorId: operator.id,
        operatorName: operatorName
      };
      this.deleteOperatorConfirmed.emit(deleteEvent);
    }
  }

  // Validation methods
  shouldShowError(index: number, fieldName: string): boolean {
    if (!this.showValidationErrors) return false;
    
    const siteGroup = this.sitesArray.at(index) as FormGroup;
    if (!siteGroup) return false;

    const field = siteGroup.get(fieldName);
    if (!field) return false;

    return field.invalid && (field.dirty || field.touched);
  }

  getErrorMessage(index: number, fieldName: string): string {
    const siteGroup = this.sitesArray.at(index) as FormGroup;
    if (!siteGroup) return '';

    const field = siteGroup.get(fieldName);
    if (!field || !field.errors) return '';

    const errors = field.errors;
    
    if (errors['required']) {
      return `${this.getFieldLabel(fieldName)} is required`;
    }
    
    if (errors['minlength']) {
      return `${this.getFieldLabel(fieldName)} must be at least ${errors['minlength'].requiredLength} characters`;
    }
    
    if (errors['maxlength']) {
      return `${this.getFieldLabel(fieldName)} must not exceed ${errors['maxlength'].requiredLength} characters`;
    }

    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      name: 'Site Name',
      region_id: 'Region'
    };
    return labels[fieldName] || fieldName;
  }

  private getFormErrors(): { [key: string]: string } {
    const errors: { [key: string]: string } = {};
    
    this.sitesArray.controls.forEach((siteControl, index) => {
      const siteGroup = siteControl as FormGroup;
      Object.keys(siteGroup.controls).forEach(key => {
        const control = siteGroup.get(key);
        if (control && control.errors && (control.dirty || control.touched)) {
          errors[`site_${index}_${key}`] = this.getErrorMessage(index, key);
        }
      });
    });

    return errors;
  }

  // Public methods for parent components
  markAllAsTouched(): void {
    this.sitesForm.markAllAsTouched();
  }

  resetForm(): void {
    this.sitesForm.reset();
    this.initializeForm();
  }

  isValid(): boolean {
    return this.sitesForm.valid;
  }

  getValue(): SitesManagerChangeEvent {
    const formValue = this.sitesForm.value;
    return {
      sites: formValue.sites || [],
      operatorAssignments: this.getOperatorAssignments(),
      isValid: this.sitesForm.valid,
      errors: this.getFormErrors()
    };
  }

  onBlur(): void {
    this.onTouched();
  }

  // UI Helper methods
  canRemoveSite(): boolean {
    return this.sitesArray.length > 1;
  }

  getSiteId(index: number): number | string | null {
    const siteGroup = this.sitesArray.at(index) as FormGroup;
    const idControl = siteGroup && siteGroup.get('id');
    return idControl ? idControl.value : null;
  }

  getSiteName(index: number): string {
    const siteGroup = this.sitesArray.at(index) as FormGroup;
    const nameControl = siteGroup && siteGroup.get('name');
    return nameControl ? nameControl.value : '';
  }

  // UI helper methods for site removal
  siteHasAssignedOperators(index: number): boolean {
    const siteId = this.getSiteId(index);
    return this.getOperatorsToCleanup(siteId).length > 0;
  }

  getAssignedOperatorsCount(index: number): number {
    const siteId = this.getSiteId(index);
    return this.getOperatorsToCleanup(siteId).length;
  }

  getOperatorDisplayName(operator: UserFormData | null): string {
    if (!operator) return '';
    return operator.userDetails && operator.userDetails.name ? operator.userDetails.name : operator.username;
  }

  isEditOperatorFieldInvalid(fieldName: string): boolean {
    const field = this.editOperatorForm.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

}